import React from 'react'
import clsx from 'clsx'

interface SkeletonProps {
  className?: string
}

export const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={clsx('animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800', className)} />
)

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({ lines = 3, className }) => (
  <div className={clsx('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={clsx('h-4', i === lines - 1 ? 'w-3/4' : 'w-full')}
      />
    ))}
  </div>
)

export function DashboardSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Header */}
      <div>
        <Skeleton className="h-8 w-72 mb-2" />
        <Skeleton className="h-4 w-56" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="glass-card p-5">
            <Skeleton className="w-9 h-9 rounded-xl mb-3" />
            <Skeleton className="h-8 w-16 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <Skeleton className="h-4 w-32 mb-4" />
          <Skeleton className="h-[220px] w-full rounded-xl" />
        </div>
        <div className="glass-card p-6">
          <Skeleton className="h-4 w-40 mb-4" />
          <Skeleton className="h-[220px] w-full rounded-xl" />
        </div>
      </div>

      {/* Sessions */}
      <div className="glass-card p-6">
        <Skeleton className="h-4 w-36 mb-4" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  )
}

export function PracticeSkeleton() {
  return (
    <div className="max-w-4xl mx-auto w-full px-4 py-6 space-y-6">
      {/* Selector */}
      <div className="card">
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="flex gap-3">
          <Skeleton className="h-10 flex-1 rounded-xl" />
          <Skeleton className="h-10 flex-1 rounded-xl" />
          <Skeleton className="h-10 w-28 rounded-xl" />
        </div>
      </div>

      {/* Ayah display */}
      <div className="card">
        <Skeleton className="h-5 w-48 mb-4" />
        <div className="flex items-center gap-3 mb-4 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-3 w-32 mb-1" />
            <Skeleton className="h-6 w-40 rounded" />
          </div>
        </div>
        <Skeleton className="h-32 w-full rounded-xl" />
        <div className="flex gap-2 mt-4 justify-center">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-20 rounded-full" />
          ))}
        </div>
      </div>

      {/* Record + Waveform */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card flex items-center justify-center min-h-[200px]">
          <Skeleton className="w-20 h-20 rounded-full" />
        </div>
        <div className="card min-h-[200px]">
          <Skeleton className="h-full w-full rounded-xl" />
        </div>
      </div>
    </div>
  )
}
